import { Download } from "@mui/icons-material";
import { Box, Button, Card, CardContent, CardHeader, CircularProgress, Container, Grid } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridSortItem, GridSortModel } from "@mui/x-data-grid";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { fetchInspectionDetail, useInspections } from "api/services/useInspections";
import { ErrorView } from "components/common/ErrorView/ErrorView";
import { getErrorTypeFromHttpResponse } from "components/common/ErrorView/utils";
import { FilterPosichecks } from "components/dataGridFilter/FilterPosichecks";
import { inspectionsfiltersData } from "components/dataGridFilter/FiltersData";
import { JANZU_URL } from "config/config";
import { useDataFilterState } from "hooks/useDataFilterState";
import { useValueDebounce } from "hooks/useValueDebounce";
import { Inspection } from "models";
import { Typography } from "new-components/typography/Typography";
import { CursorPagination } from "pages/data/DataPage";
import { extractPaginateFrom } from "utils/utilityFunctions";

import { FilterSearchToolbar } from "./components/FilterSearchToolbar";

function useDownloadUrlFetch(rows: Inspection[], setRows: React.Dispatch<React.SetStateAction<Inspection[]>>) {
  const [id, setId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const generateUrl = async (idValue: string) => {
    setId(idValue);
    setLoading(true);
    try {
      const data = await fetchInspectionDetail(idValue);
      const mapRows = rows.map((row) => (row.id === idValue ? { ...row, download_url: data?.download_url } : row));
      setRows(mapRows);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error generating download URL", { variant: "error" });
    } finally {
      setId(null);
      setLoading(false);
    }
  };

  return { generateUrl, loading, id };
}
const DEFAULT_PAGE_SIZE = 100;

export function ListInspectionsRoute() {
  const navigate = useNavigate();

  const [rowCount, setRowCount] = useState(0);
  const [rows, setRows] = useState<Inspection[]>([]);
  const [searchText, setSearchText] = useState("");
  const inspectionsUrl = `${JANZU_URL}device-api/inspections?`;
  const debouncedSearchTerm = useValueDebounce<string>(searchText, 500);
  const { generateUrl, loading, id } = useDownloadUrlFetch(rows, setRows);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [cursorPagination, setCursorPagination] = useState<CursorPagination>({
    cursor: "",
    direction: "next",
  });

  // HOOKS
  const { parameterizedUrl, filterState, onFilterModelChange } = useDataFilterState({
    serverFilterBaseUrl: inspectionsUrl,
    pageSize,
    cursorImageQueryId: cursorPagination,
    searchTerm: debouncedSearchTerm,
  });

  const {
    inspections: dataListInspections,
    inspectionsError: errorListInspections,
    isLoadingInspections: loadingListInspections,
  } = useInspections({
    listUrl: parameterizedUrl,
    detailEnabled: false,
  });

  const columns: GridColDef[] = [
    { field: "user_provided_id_value", headerName: "ID", flex: 1, minWidth: 200 },
    {
      field: "created_at",
      headerName: "Timestamp",
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        const { value } = params;
        const valueStr = value?.toString();
        if (!valueStr) {
          return "";
        }
        const date = new Date(valueStr);
        return <Box>{date.toLocaleString()}</Box>;
      },
    },
    { field: "result", headerName: "Result", minWidth: 150 },
    {
      field: "report_key",
      headerName: "PDF Report?",
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => {
        const { value } = params;
        const valueStr = value?.toString();
        return valueStr ? "YES" : "NO";
      },
    },
    {
      field: "download_url",
      headerName: "Download",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        // Handler for button click
        const { value, row: inspection } = params;

        if (inspection.report_key == null) {
          return <Typography>N/A</Typography>;
        }

        const handleGenerateDownloadLink = (e: React.MouseEvent) => {
          generateUrl(inspection.id);
          e.stopPropagation();
        };

        const handleDownloadLink = (e: React.MouseEvent) => {
          window.open(value, "_self");
          e.stopPropagation();
        };

        const isLoading = loading && id === inspection.id;

        return (
          <Button
            variant="contained"
            className="capitalize tracking-normal shadow-none px-2 w-[190px]"
            style={{
              backgroundColor: value ? "#006DD9" : "#E5E9ED",
              color: value ? "#FFF" : "#000",
              letterSpacing: "10px!important",
            }}
            endIcon={<Download />}
            onClick={value ? handleDownloadLink : handleGenerateDownloadLink}
          >
            {isLoading ? (
              <CircularProgress size={20} className="text-black" />
            ) : (
              <Box>{value ? "Download PDF Report" : "Generate download link"}</Box>
            )}
          </Button>
        );
      },
    },
  ];

  // HANDLES
  const updateSearchText = (searchedVal: string) => {
    // update the displayed text in the search bar
    setSearchText(searchedVal);
  };

  const handleChangePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleChangePage = (newPage: number) => {
    if (newPage > page) {
      setCursorPagination({
        cursor: extractPaginateFrom(dataListInspections?.next || ""),
        direction: "next",
      });
    } else {
      setCursorPagination({
        cursor: extractPaginateFrom(dataListInspections?.previous || ""),
        direction: "previous",
      });
    }
    setPage(newPage);
  };

  // update state when a sort is requested
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    const tempOrderingText: string[] = [];

    newSortModel.forEach((item) => {
      let { field } = item;
      if (item.sort === "desc") {
        field = `-${field}`;
      }
      tempOrderingText.push(field);
    });
  };

  // EFFECTS
  useEffect(() => {
    if (!errorListInspections && Array.isArray(dataListInspections?.results)) {
      setRowCount(dataListInspections?.count || 0);
      setRows(dataListInspections?.results || []);
    }
  }, [errorListInspections, dataListInspections, setRowCount, setRows]);

  // MEMOS

  const orderingModel: GridSortItem[] = useMemo(() => {
    if (filterState.ordering) {
      const isDescending = filterState.ordering.startsWith("-");
      const field = isDescending ? filterState.ordering.substring(1) : filterState.ordering;

      return [
        {
          field,
          sort: isDescending ? "desc" : "asc",
        },
      ];
    }
    return [];
  }, [filterState]);

  return (
    <Container>
      <Box className="flex-col space-y-4">
        <Box className="mb-4">
          <FilterPosichecks
            filterState={filterState}
            isDisabled={rowCount === 0}
            onFilterChange={onFilterModelChange}
            filterableElements={inspectionsfiltersData}
          />
        </Box>
        <DataGrid
          components={{
            Toolbar: FilterSearchToolbar,
          }}
          sx={{
            // disable cell selection style
            ".MuiDataGrid-cell:focus": {
              outline: "none",
            },
            // pointer cursor on ALL rows
            "& .MuiDataGrid-row:hover": {
              cursor: "pointer",
            },
          }}
          componentsProps={{
            toolbar: {
              text: searchText,
              updateSearchText,
              requestSearch: updateSearchText,
            },
          }}
          initialState={{
            pagination: {
              pageSize: DEFAULT_PAGE_SIZE,
            },
          }}
          rows={rows}
          rowCount={rowCount}
          columns={columns}
          loading={loadingListInspections}
          onRowClick={(params) => navigate(`/reef/data/inspections/${params.id}`)}
          className="h-[75vh]"
          rowsPerPageOptions={[25, 50, 100]}
          pagination
          page={page - 1}
          pageSize={pageSize}
          sortModel={orderingModel}
          paginationMode="server"
          filterMode="server"
          sortingMode="server"
          onPageChange={(newPage: number) => handleChangePage(newPage + 1)}
          onPageSizeChange={(newPageSize: number) => handleChangePageSize(newPageSize)}
          onSortModelChange={handleSortModelChange}
        />
        <Box>
          <Link to="/reef/detectors/create">Need to create a new detector?</Link>
        </Box>
      </Box>
    </Container>
  );
}

export function InspectionDetailRoute() {
  const { id } = useParams<{ id?: string }>();

  return id ? <InspectionDetail inspectionId={id} /> : <Box>Please provide an id in the URL!</Box>;
}

interface InspectionDetailProps {
  inspectionId: string;
}

export function InspectionDetail(props: InspectionDetailProps) {
  const { inspectionId } = props;

  const {
    inspection,
    inspectionResponse: response,
    inspectionError: error,
    isLoadingInspection: loading,
  } = useInspections({
    detailId: inspectionId,
    listEnabled: false,
  });

  const status = loading || error ? <CircularProgress size={25} /> : null;

  const date = inspection ? new Date(inspection?.created_at) : undefined;

  const errorType = response ? getErrorTypeFromHttpResponse(response) : null;

  if (errorType) {
    return <ErrorView error={errorType} />;
  }

  return (
    status || (
      <Grid
        container
        spacing={2}
        direction="column"
        sx={{
          justifyContent: "center",
          alignItems: "center",
          alignContent: "center",
        }}
      >
        <Grid item>
          <Typography variant="subtitle">Inspection</Typography>
        </Grid>
        <Grid item>
          <Card>
            <CardHeader title={inspection?.user_provided_id_value || inspection?.id} />
            <CardContent>
              <Grid
                container
                spacing={2}
                direction="column"
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  alignContent: "center",
                }}
              >
                <Grid item>
                  <Typography>{date?.toLocaleString()}</Typography>
                </Grid>
                <Grid item>
                  <Typography>Result: {inspection?.result}</Typography>
                </Grid>
                {inspection?.download_url ? (
                  <Grid item>
                    <Typography>
                      <Link to={inspection.download_url}>Download PDF Report</Link>
                    </Typography>
                  </Grid>
                ) : undefined}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    )
  );
}
